import React, { useMemo } from 'react';
import { Field, FieldTypes } from '@common-fe/common-fe';
import * as yup from 'yup';

import { REQUIRED_TEXT } from '@/common/constants';
import regexp from '@/common/regexp';
import { BankInfo } from '@/modules/employer/components/SetupEmployer/SetupEmployer.types';

import { BankAccountField } from '../BankAccountField';
import useBankInformationQuery from '../useBankInformation.query';
import { IAccountDtoAccountCategoryEnum } from '@/dtos';

const REQUIRED_FILED = 'This field is required.';
const last4length = 4;

export type BankInformationFields = BankInfo & {
  employerBankingContactType?: 'new' | 'exist';
};

type Fields = Field<keyof BankInformationFields>[];

interface Props {
  defaultFields?: BankInformationFields;
  editMode?: boolean;
  viewMode?: boolean;
  hasViewAccess?: boolean;
  bankAccount?: string;
  handleCopyToClipboardAccountNumber?: () => void;
}

export default ({
  defaultFields,
  editMode,
  viewMode,
  hasViewAccess,
  bankAccount,
  handleCopyToClipboardAccountNumber,
}: Props) => {
  const { bankAccountCategories = [] } = useBankInformationQuery();
  const secureMask = useMemo(() => {
    if (defaultFields?.number) {
      return `${Array(defaultFields.number.length - last4length)
        .fill('•')
        .join('')}9999`;
    }
    return '';
  }, [defaultFields]);

  const currentEditField = useMemo(() => {
    if (hasViewAccess && editMode) {
      return {
        name: 'number',
        label: 'Account number',
        type: FieldTypes.BankAccount,
        placeholder: 'Enter bank account',
        value: bankAccount,
        maskedValue: defaultFields?.number || '',
        onCopy: handleCopyToClipboardAccountNumber,
        eyeIcon: defaultFields?.number?.length !== last4length,
        validator: yup
          .string()
          .test('len', 'Must be between 4 and 17 characters', (val) => {
            return !val || (val.length >= 4 && val.length <= 17);
          })
          .required(REQUIRED_FILED),
      } as unknown as Field<keyof BankInformationFields>;
    }
    return {
      name: 'number',
      type: FieldTypes.Mask,
      label: 'Account number',
      mask: '99999999999999999',
      formatChars: { '9': regexp.ALPHANUMERIC_WITH_DOTS },
      secure: true,
      secureMask,
      placeholder: 'Account number',
      isFullClear: editMode,
      validator: yup
        .string()
        .test('len', 'Must be between 4 and 17 characters', (val) => {
          return !val || (val.length >= 4 && val.length <= 17);
        })
        .required(REQUIRED_FILED),
      defaultValue: defaultFields?.number || '',
      value: defaultFields?.number || '',
    } as unknown as Field<keyof BankInformationFields>;
  }, [
    bankAccount,
    defaultFields?.number,
    handleCopyToClipboardAccountNumber,
    hasViewAccess,
    secureMask,
    editMode,
  ]);

  const fields: Fields = useMemo((): Fields => {
    const options = bankAccountCategories.map((option) => ({
      label: option.description,
      value: option.name,
    }));
    const source: Fields = [
      {
        name: 'description',
        type: FieldTypes.Text,
        label: 'Name on account',
        placeholder: 'Name on account',
        validator: yup.string().trim().required(REQUIRED_FILED),
        defaultValue: defaultFields?.description || '' || '',
      },
      {
        name: 'routingTransitNumber',
        type: FieldTypes.Mask,
        label: 'Routing number',
        mask: '999999999',
        placeholder: 'Routing number',
        validator: yup
          .string()
          .test('len', 'Must be exactly 9 characters', (val) => !val || val.length === 9)
          .required(REQUIRED_FILED),
        defaultValue: defaultFields?.routingTransitNumber || '',
      },
      ...(editMode || !viewMode
        ? [currentEditField]
        : [
            {
              name: 'number_node',
              type: FieldTypes.Node,
              label: 'Account number',
              value: (
                <BankAccountField
                  last4Value={defaultFields?.accountNumberLast4 || ''}
                  accountNumberLength={defaultFields?.accountNumberLength || 0}
                  accountId={defaultFields?.id || ''}
                />
              ),
            } as unknown as Field<keyof BankInformationFields>,
          ]),
      {
        name: 'accountCategory',
        type: FieldTypes.Radio,
        label: 'Bank account type',
        validator: yup
          .string()
          .notOneOf([IAccountDtoAccountCategoryEnum.NONE], REQUIRED_TEXT)
          .required(REQUIRED_TEXT),
        defaultValue: defaultFields?.accountCategory || '',
        options,
      },
    ];
    return source;
  }, [defaultFields, bankAccountCategories, editMode, currentEditField, viewMode]);

  return fields;
};
